import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  Autocomplete,
  DialogContentText,
  Backdrop,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

import {
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";

function Faqs() {
  const [faqList, setFaqList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    question_en: "",
    question_fr: "",
    answer_en: "",
    answer_fr: "",
    role: "",
  });

  const initialState = {
    question_en: "",
    question_fr: "",
    answer_en: "",
    answer_fr: "",
    role: "",
    status: "active",
  };

  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const roles = [{ name: "driver" }, { name: "customer" }];
  const [selectedRole, setSelectedRole] = useState("");

  const getFaqList = useCallback(
    async (page, selectedRole) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems, user_type : selectedRole };
        const response = await apiRequest.get(
          endPoints.GET_FAQS,
          params,
          {}
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setFaqList(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        setError("Failed to fetch FAQs. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [totalItems]
  );

  useEffect(() => {
    getFaqList(currentPage, selectedRole);
  }, [currentPage, getFaqList, selectedRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = {
        question_en: formData.question_en,
        question_fr: formData.question_fr,
        answer_en: formData.answer_en,
        answer_fr: formData.answer_fr,
        user_type: formData.role,
      };

      const endPoint = isEditing
        ? `${endPoints.UPDATE_FAQ}/${currentId}`
        : `${endPoints.ADD_FAQ}`;
      const requestMethod = isEditing
        ? apiRequest.put
        : apiRequest.post;

      const response = await requestMethod(endPoint, data, {
        headers: {},
      });

      if (response.success) {
        setSuccess(
          isEditing
            ? "Faq updated successfully."
            : "Faq added successfully."
        );
        getFaqList(currentPage);
        handleCloseModal();
      } else {
        setError(response.message || "Failed to add/update Faq.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError(
        "An error occurred while processing your request."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    setFormData(initialState);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData(initialState);
    setIsEditing(false);
  };

  const handleEdit = (faq) => {
    setOpenModal(true);
    setIsEditing(true);
    setFormData({
      question_en: faq.question_en || "",
      question_fr: faq.question_fr || "",
      answer_en: faq.answer_en || "",
      answer_fr: faq.answer_fr || "",
      role: faq.user_type || "",
    });
    setCurrentId(faq.id);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await apiRequest.delete(
        `${endPoints.DELETE_FAQ}/${id}`,
        {
          headers: {},
        }
      );

      if (response.success) {
        setSuccess("FAQ deleted successfully.");
        setFaqList((prev) =>
          prev.filter((newData) => newData.id !== id)
        );
      } else {
        setError(
          response.message || "Failed to delete the FAQ."
        );
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      setError(
        "An error occurred while deleting the FAQ."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus =
        currentStatus === "active" ? "inactive" : "active";
      const payload = { status: newStatus };

      const response = await apiRequest.put(
        `${endPoints.UPDATE_FAQ}/${id}`,
        payload,
        {}
      );

      if (response.success) {
        setSuccess("Faq status updated successfully.");
        setFaqList((prev) =>
          prev.map((faq) =>
            faq.id === id
              ? { ...faq, status: newStatus }
              : faq
          )
        );
      } else {
        setError(
          response.message ||
            "Failed to update FAQ status."
        );
      }
    } catch (error) {
      console.error("Error updating FAQ status:", error);
      setError(
        "An error occurred while updating the FAQ status."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRoleChange = (event, newValue) => {
    setSelectedRole(newValue ? newValue.name : "");
    setCurrentPage(1);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !selectedRole) return;

    const reordered = Array.from(faqList);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setFaqList(reordered);

       // Prepare the sequence data
       const sequenceData = reordered.map((item, index) => ({
        id: item.id,
        sequence: index + 1
      }));

      const headers = {
        
      }

    try {
      await apiRequest.put(
              endPoints.SEQUENCE_FAQ,
              sequenceData,
              { headers }
            );
      setSuccess("FAQs reordered successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to reorder FAQs.");
    }
  };

  const renderFaqCard = (faq, provided = null, snapshot = null) => {
    return (
      <Accordion
        ref={provided?.innerRef || null}
        {...(provided?.draggableProps || {})}
        {...(provided?.dragHandleProps || {})}
        sx={{
          mb: 2,
          boxShadow: 3,
          backgroundColor: snapshot?.isDragging ? "#e3f2fd" : "#fff",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <MDTypography variant="h6" color="dark">
                {faq.question_en} / {faq.question_fr}
              </MDTypography>
              <MDTypography variant="caption" color="textSecondary">
                ({faq.user_type})
              </MDTypography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={faq.status === "active"}
                    onChange={() => handleStatusToggle(faq.id, faq.status)}
                    color="primary"
                  />
                }
                label={faq.status === "active" ? "Active" : "Inactive"}
              />
              <IconButton color="primary" onClick={() => handleEdit(faq)}>
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => {
                  setCurrentId(faq.id);
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Divider sx={{ my: 2 }} />
          <MDTypography variant="subtitle2" color="info">
            English Answer
          </MDTypography>
          <Box
            dangerouslySetInnerHTML={{ __html: faq.answer_en }}
            sx={{ fontSize: "1rem", lineHeight: "1.6", mb: 2 }}
          />
          <MDTypography variant="subtitle2" color="info">
            Arabic Answer
          </MDTypography>
          <Box
            dangerouslySetInnerHTML={{ __html: faq.answer_fr }}
            sx={{ fontSize: "1rem", lineHeight: "1.6" }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderFaqList = () => {
    const filteredFaqs = faqList;

    if (selectedRole) {
      return (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="faqs">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {filteredFaqs.map((faq, index) => (
                  <Draggable
                    key={faq.id}
                    draggableId={String(faq.id)}
                    index={index}
                  >
                    {(provided, snapshot) =>
                      renderFaqCard(faq, provided, snapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      );
    } else {
      return filteredFaqs.map((faq) => renderFaqCard(faq));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDBox mb={3}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    FAQs
                  </MDTypography>
                </MDBox>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <Autocomplete
                    options={roles}
                    getOptionLabel={(option) => option?.name || ""}
                    value={roles.find((r) => r.name === selectedRole) || null}
                    onChange={handleRoleChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by role"
                        variant="outlined"
                        style={{ width: 300 }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                  />
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{ whiteSpace: "nowrap", color: "white" }}
                  >
                    Add FAQ
                  </Button>
                </MDBox>

                <MDBox p={3}>
                  {renderFaqList()}
                </MDBox>

                <MDBox
                  mt={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Add/Edit FAQ Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{isEditing ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
        <DialogContent>
          <Grid item xs={12} sm={12}>
            <Autocomplete
              options={roles}
              getOptionLabel={(option) => option?.name || ""}
              value={
                formData.role
                  ? roles.find((role) => role.name === formData.role)
                  : null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  role: newValue ? newValue.name : null,
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Role" />
              )}
              required
            />
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Question (English)"
                fullWidth
                value={formData.question_en}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    question_en: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Question (Arabic)"
                fullWidth
                value={formData.question_fr}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    question_fr: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="subtitle1" gutterBottom>
                Answer (English)
              </MDTypography>
              <CKEditor
                editor={ClassicEditor}
                data={formData.answer_en}
                onChange={(event, editor) =>
                  setFormData({
                    ...formData,
                    answer_en: editor.getData(),
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="subtitle1" gutterBottom>
                Answer (Arabic)
              </MDTypography>
              <CKEditor
                editor={ClassicEditor}
                data={formData.answer_fr}
                onChange={(event, editor) =>
                  setFormData({
                    ...formData,
                    answer_fr: editor.getData(),
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            style={{ whiteSpace: "nowrap", color: "white" }}
            onClick={handleSubmit}
          >
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this FAQ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              handleDelete(currentId);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={3000}
        onClose={() => {
          setSuccess(null);
          setError(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => {
            setSuccess(null);
            setError(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardLayout>
  );
}

export default Faqs;